<!--
  @name: EchartsBoxPie
  @description: echarts线下图基础样式组件
-->
<template>
  <custom-echarts-box
    ref="echartsRef"
    v-bind="$attrs"
    :option="defaultBasicOption"
    :loading="loading"
  />
</template>
<script setup lang="ts">
import { PropType, reactive, ref, watch } from 'vue';
import CustomEchartsBox from './index.vue';
import { type ECOption, type SeriesOption } from './setupEcharts';
import { isNull } from '@core/tools';

defineOptions({ name: 'CustomEchartsPie' });

const props = defineProps({
  dataSet: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  tooltipName: {
    type: String,
    default: '',
  },
  // 系列配置
  seriesOption: {
    type: Object as PropType<SeriesOption>,
    default: () => ({} as SeriesOption),
  },
  // 图表配置
  option: {
    type: Object as PropType<ECOption>,
    default: () => ({} as ECOption),
  },
});

const echartsRef = ref();

// 每个系列默认配置
const defaultSeriesOption = reactive<SeriesOption>({
  type: 'pie',
  radius: ['30%', '50%'],
  left: '0%',
  bottom: '10%',
  label: {
    formatter: '{d} %\n',
    show: true, 
    lineHeight: 30,
    position: 'outer',
    alignTo: 'labelLine',
    bleedMargin: 10,
    color: 'inherit',
  },
  labelLine: {
    show: true,
    length: 10,
    length2: 40,
    maxSurfaceAngle: 40,
  },
  labelLayout: function(params) {
    const isLeft = params.labelRect.x < echartsRef.value?.getEcInstance().getWidth() / 2;
    const points = params.labelLinePoints;

    // Update the end point.
    points[2][0] = isLeft
      ? params.labelRect.x
      : params.labelRect.x + params.labelRect.width;
    return { labelLinePoints: points };
  },
  itemStyle: {
    borderColor: '#fff',
    borderWidth: 2,
    color: function(colors) {
      var colorList = ['#73A0FA', '#72DEB3', '#83D0EF', '#FFAB67', '#7585A2', '#75bedc'];

      return colorList[colors.dataIndex];
    },
  },
});

// echarts默认基础图表配置
const defaultBasicOption = ref<ECOption>({
  // 标题组件
  title: {},
  // 网格组件
  grid: {
    left: 0,
    right: 0,
    top: 60,
    bottom: 0,
    containLabel: true,
  },
  // 控制图标组件
  legend: {
    icon: 'circle',
    top: 'bottom',
    left: '15%',
    align: 'left',
    itemWidth: 12,
    itemHeight: 12,
    width: '90%',
    itemGap: 30,
    textStyle: {
      color: '#00000099',
      fontSize: 12,
    },
  },
  tooltip: {
    trigger: 'item',
    padding: 16,
    textStyle: {
      fontSize: 12,
      color: '#000',
    },
    axisPointer: { type: 'none' },
    formatter: '{b} {d} %',
  },

  dataZoom: {
    type: 'inside',
    start: 0,
    endValue: 6, // 设置超过传入数据的索引值的数据进行滑动
    zoomLock: true,
  },
  series: [],
});

watch(
  () => props.option,
  (newV) => {
    echartsRef.value?.getEcInstance().setOption(newV);
  },
  { immediate: true },
);

watch(
  () => props.seriesOption,
  (newV) => {
    Object.assign(defaultSeriesOption, newV);
  },
  { immediate: true },
);

watch(
  () => props.dataSet,
  (newV) => {
    if (!isNull(newV) && newV.length > 0) {        
      echartsRef.value?.getEcInstance().setOption(
        { series: [Object.assign({ data: newV }, defaultSeriesOption)] },
        { replaceMerge: 'series' },
      );
    }
  },
  { immediate: true },
);
</script>